import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  formatCorrectionFieldLabel,
  formatCorrectionFromTo,
  getBondRetrieveUrl,
} from "../hooks/useApproveBonds";

export default function ApproveBondsDataCorrectionDetailDialog({
  dataCorrectionDetailOpen,
  setDataCorrectionDetailOpen,
  selectedDataCorrection,
  setSelectedDataCorrection,
  completingCorrectionId,
  getCorrectionId,
  updateCorrectionStatusToComplete,
}) {
  return (
    <Dialog
      open={dataCorrectionDetailOpen}
      onClose={() => {
        setDataCorrectionDetailOpen(false);
        setSelectedDataCorrection(null);
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } }}}
    >
      <DialogTitle sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1.5, mb: 2 }}>
        Correction details
      </DialogTitle>
      <DialogContent sx={{ pt: 4 }}>
        {selectedDataCorrection && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Date</Typography>
              <Typography variant="body1">
                {selectedDataCorrection.created_at ? new Date(selectedDataCorrection.created_at).toLocaleString() : "—"}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Bond type</Typography>
              <Typography variant="body1">{selectedDataCorrection.bond_type ?? "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Bond number</Typography>
              <Typography variant="body1">{selectedDataCorrection.bond_number ?? "—"}</Typography>
            </Box>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Change</Typography>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Field</Typography>
              <Typography variant="body1">{formatCorrectionFieldLabel(selectedDataCorrection.changes?.field)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>From</Typography>
              <Typography variant="body1">{formatCorrectionFromTo(selectedDataCorrection.changes?.field, selectedDataCorrection.changes?.from) || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>To</Typography>
              <Typography variant="body1">{formatCorrectionFromTo(selectedDataCorrection.changes?.field, selectedDataCorrection.changes?.to) || "—"}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Requester</Typography>
              <Typography variant="body1">
                {selectedDataCorrection.requester_info?.name ?? "—"}
                {selectedDataCorrection.requester_info?.role ? ` (${selectedDataCorrection.requester_info.role})` : ""}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Status</Typography>
              <Typography variant="body1">{selectedDataCorrection.status ?? "—"}</Typography>
            </Box>
            {selectedDataCorrection.bond_id && (
              <>
                <Divider />
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const url = getBondRetrieveUrl(selectedDataCorrection.bond_type, selectedDataCorrection.bond_id);
                    if (url) window.open(url, "_blank");
                  }}
                >
                  View bond details
                </Button>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        {selectedDataCorrection && (selectedDataCorrection.status || "").toString().trim().toLowerCase() !== "complete" && (
          <Button
            variant="contained"
            color="success"
            startIcon={completingCorrectionId === getCorrectionId(selectedDataCorrection) ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            disabled={completingCorrectionId === getCorrectionId(selectedDataCorrection)}
            onClick={() => updateCorrectionStatusToComplete(selectedDataCorrection)}
          >
            Mark as Complete
          </Button>
        )}
        <Button
          onClick={() => {
            setDataCorrectionDetailOpen(false);
            setSelectedDataCorrection(null);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ApproveBondsDataCorrectionDetailDialog.propTypes = {
  dataCorrectionDetailOpen: PropTypes.bool.isRequired,
  setDataCorrectionDetailOpen: PropTypes.func.isRequired,
  selectedDataCorrection: PropTypes.object,
  setSelectedDataCorrection: PropTypes.func.isRequired,
  completingCorrectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getCorrectionId: PropTypes.func.isRequired,
  updateCorrectionStatusToComplete: PropTypes.func.isRequired,
};
